import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import DebouncePromise from 'awesome-debounce-promise';
import { useAsyncCallback } from 'react-async-hook';
import { useParams } from 'react-router-dom';
import {
  FinanceResponse,
  FinanceResponseData,
  FinanceResponseId,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import { Deal, DealId } from '@upstart/harmony/lib/legos/dealTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { FinanceResponseStatus } from '@upstart/harmony/lib/legos/constants';
import { Alert, Col, Grid, Row } from 'react-bootstrap';
import { bindActionCreators, Dispatch } from 'redux';
import { Loader } from 'semantic-ui-react';
import { getUserMessage } from '@upstart/harmony/lib/error-utils';
import { Backdrop, Snackbar, CircularProgress } from '@mui/material';

import SidebarContainer from '../SidebarContainer';
import dealsActions from '../../redux/deals/actions';
import { HeaderNavigation, HeaderNotice } from '../../components/PencilEditor';
import { RICForm } from './RICForm';
import FinanceResponseService from '../../services/FinanceResponseService';
import FinanceApplicationService from '../../services/FinanceApplicationService';
import PaymentsService from '../../services/PaymentsService';
import CDKSaveDealWarningModal from './RICForm/CDKSaveDealWarningModal';
import { DQReasonsPanel } from './RICForm/Components/DQReasonsPanel';
import { subscribeToWebsocketRicSignedEvent } from './WebsocketSubscription';
import { useFniPresent } from '../../hooks/useFniPresent';
import { getUpstartOfferEventProperties, sendData } from '../../util/amplitude';
import {
  amplitudeEvents,
  amplitudeEventsDeskingCTR,
} from '../../util/amplitudeEvents';
import InstoreService from '../../services/InstoreService';
import {
  isEmbeddedInClosingWorkflow,
  isEmbeddedInCommandCenter,
  isEmbeddedInFniHome,
  sendIframeMaxHeightToCC,
  sendReviewFinanceCompleteToCC,
  sendRicFormUpdateToCC,
} from '../../util/commandCenter';
import { useGetFinanceResponseForRIC } from '../../hooks/useGetFinanceResponseForRIC';

const IS_EMBEDDED_IN_CC = isEmbeddedInCommandCenter();

const DEBOUNCE_WAIT = 1000; // milliseconds
const RIC_DOWNLOAD_URL_WAIT = 7000; // 7 seconds
const RIC_DOWNLOAD_URL_MAX_TRIES = 10;
const FNI_PRESENT_LOADING_MESSAGE =
  'RIC is being prepared. Please stay on this page.';

interface Params {
  dealId: DealId;
  responseId: FinanceResponseId;
}

interface Props {
  car?: Car;
  deal?: Deal;
  dealer?: Dealer;
  currentUser: User;
  user?: User;
  isLoading?: boolean;
  actions: {
    loadDealData: ({ dealId }: { dealId: DealId }) => void;
  };
  dealId: string;
  responseId: string;
  hideSideBarAndHeader?: boolean;
  onCancel?: () => void;
}

const getPaymentsForFinanceResponse = DebouncePromise(
  PaymentsService.getPaymentsForFinanceResponse,
  DEBOUNCE_WAIT,
);

/**
 * Check if the contract has been signed for the application.
 */
const isSigned = (status: UpstartApplicationStatus) =>
  !_.isNil(status.signedOn);

function OfferReview(props: Props) {
  const params = useParams<Params>();
  const responseId = params.responseId || props.responseId;
  const dealId = params.dealId || props.dealId;
  const hideSideBarAndHeader =
    props.hideSideBarAndHeader || isEmbeddedInFniHome() || false;
  const ricState = props.dealer?.state;
  const car = props.car;
  const isFniPresentFlowEnabled = useFniPresent({
    currentUser: props.currentUser,
    dealer: props.dealer,
  });
  const [response, setResponse] = useState<FinanceResponse>();
  const [ricDownloadURL, setRicDownloadURL] = useState<string>('');
  const [fetchRICDownloadURLCount, setFetchRICDownloadURLCount] = useState<
    number
  >(1);
  const [fetchingRICDownloadURL, setFetchingRICDownloadURL] = useState<boolean>(
    false,
  );
  const [showDQReasonsPanel, setShowDQReasonsPanel] = useState<boolean>(false);
  const [fniPresentLoading, setFniPresentLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const ricFormRef = useRef<HTMLFormElement>(null);
  const asyncReviewFinancing = useAsyncCallback<FinanceResponse>(
    FinanceResponseService.reviewFinancing,
  );

  const {
    isDMSFlowEnabled,
    showCDKSaveDealNotice,
    asyncResponse,
    dmsPullError,
  } = useGetFinanceResponseForRIC({
    responseId,
    deal: props.deal,
    dmsType: props?.dealer?.dmsType,
  });

  const asyncPayments = useAsyncCallback<FinanceResponse>(
    getPaymentsForFinanceResponse,
  );
  const asyncStatus = useAsyncCallback<UpstartApplicationStatus>(
    FinanceApplicationService.getApplicationStatus,
  );

  useEffect(() => {
    if (response && isEmbeddedInClosingWorkflow()) {
      sendIframeMaxHeightToCC({ maxHeight: document.body.scrollHeight });
    }
  }, [response]);

  const onCancel = () => {
    asyncResponse.execute().then(setResponse);
  };

  const onPullFromDMS = async () => {
    const amplitudeData = {
      ...getUpstartOfferEventProperties({
        dealId,
        application: response?.FinanceApplication,
        response,
      }),
    };
    sendData(amplitudeEventsDeskingCTR.PULL_FROM_DMS, amplitudeData);
    sendData(amplitudeEventsDeskingCTR.FINANCING_PULLDMS_START, amplitudeData);

    try {
      const financeResponse = await asyncResponse.execute(true);

      if (!isEmbeddedInClosingWorkflow()) {
        // I am not sure why we need to call this, because everything that happens in
        // fetchResponseWithUpdatedData already happens in the `asyncResponse.result` useEffect below.
        // To not cause any regressions with legacy RIC functionality, only removing this in closing workflow
        fetchResponseWithUpdatedData(financeResponse.data);
      }
      sendData(
        amplitudeEventsDeskingCTR.FINANCING_PULLDMS_FINISH,
        amplitudeData,
      );
    } catch (e) {
      sendData(
        amplitudeEventsDeskingCTR.FINANCING_PULLDMS_ERROR,
        amplitudeData,
      );
      throw e;
    }
  };

  useEffect(() => {
    const messageReceiver = async (event: MessageEvent) => {
      switch (event.data.type) {
        case 'closingWorkflowPullFromDms': {
          onPullFromDMS();
          break;
        }
        case 'closingWorkflowSaveAndStartEContracting': {
          ricFormRef.current?.requestSubmit();
          break;
        }
        case 'closingWorkflowCancelAndRevert': {
          onCancel();
          break;
        }
      }
    };
    window.addEventListener('message', messageReceiver);

    return () => {
      window.removeEventListener('message', messageReceiver);
    };
  }, [window, onPullFromDMS, onCancel]);

  useEffect(() => {
    if (asyncResponse.result) {
      sendRicFormUpdateToCC({
        isRecalculating: true,
        ricFormValues: asyncResponse.result,
      });
      // Fetch the latest application status on page load - to not render the form if the contract is signed
      asyncStatus
        .execute(asyncResponse.result.applicationId)
        .then(status => {
          if (
            asyncResponse.result?.status === FinanceResponseStatus.DENIED &&
            status.adverseActionNoticeInfo
          ) {
            setShowDQReasonsPanel(true);
          }
          const hasBackendCalculationVersion = !!asyncResponse.result?.data
            ?.backendCalculationVersion;
          return asyncPayments.execute(asyncResponse.result, {
            includeBackendAmount: hasBackendCalculationVersion,
            includeValidations: true,
          });
        })
        .then(calculatedResponse => {
          setResponse(calculatedResponse);
          if (isEmbeddedInClosingWorkflow()) {
            sendRicFormUpdateToCC({
              isRecalculating: false,
              ricFormValues: calculatedResponse,
            });
          }
        });
    }
    // eslint-disable-next-line
  }, [asyncResponse.result]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (asyncStatus.result && isSigned(asyncStatus.result)) {
      // RIC is signed but there's no download URL. DAPI takes a few seconds before the URL is generated.
      // We need to retry till it's available.
      if (asyncStatus.result?.downloadUrls?.length === 0) {
        if (fetchRICDownloadURLCount > RIC_DOWNLOAD_URL_MAX_TRIES)
          return () => {};
        setFetchingRICDownloadURL(true);

        timer = setTimeout(async () => {
          if (asyncResponse?.result) {
            await asyncStatus.execute(asyncResponse.result.applicationId);
          }
          setFetchRICDownloadURLCount(fetchRICDownloadURLCount + 1);
        }, RIC_DOWNLOAD_URL_WAIT);
      } else {
        setRicDownloadURL(
          asyncStatus.result?.downloadUrls?.find(
            f => f.type === 'retail_installment_contract',
          )?.url || '',
        );
        setFetchingRICDownloadURL(false);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line
  }, [asyncStatus.result]);

  useEffect(() => {
    if (props.deal) {
      asyncResponse.execute();
    }
    // eslint-disable-next-line
  }, [props?.deal?.id]);

  useEffect(() => {
    if (dealId) {
      props.actions.loadDealData({ dealId });
    }
  }, [dealId, props.actions]);

  useEffect(() => {
    const unsubscribe = subscribeToWebsocketRicSignedEvent(dealId, async () => {
      asyncResponse.execute();
    });

    return () => unsubscribe();
    // eslint-disable-next-line
  }, []);

  const fetchResponseWithUpdatedData = (
    data?: Partial<FinanceResponseData>,
  ) => {
    if (response) {
      sendRicFormUpdateToCC({
        isRecalculating: true,
        ricFormValues: response,
      });
      const prev = response;
      const updated = { ...prev, data: { ...prev.data, ...data } };
      setResponse(updated);
      const hasBackendCalculationVersion = !!updated.data
        .backendCalculationVersion;
      asyncPayments
        .execute(updated, {
          includeBackendAmount: hasBackendCalculationVersion,
          includeValidations: true,
        })
        .then(updatedFinanceResponse => {
          setResponse(updatedFinanceResponse);
          if (isEmbeddedInClosingWorkflow()) {
            sendRicFormUpdateToCC({
              ricFormValues: updatedFinanceResponse,
              isRecalculating: false,
            });
          }
        })
        .catch(() => setResponse(prev));
    }
  };

  useEffect(() => {
    if (asyncReviewFinancing.error) {
      if (isEmbeddedInClosingWorkflow()) {
        sendReviewFinanceCompleteToCC({
          success: false,
          error: getUserMessage(asyncReviewFinancing.error.message),
        });
      } else {
        setErrorMessage('Error when reviewing financing. Please try again');

        if (_.isString(asyncReviewFinancing.error?.message)) {
          setErrorMessage(getUserMessage(asyncReviewFinancing.error?.message));
        }
      }
    } else if (dmsPullError) {
      setErrorMessage(dmsPullError);
    } else if (
      asyncResponse.error ||
      asyncPayments.error ||
      asyncStatus.error
    ) {
      setErrorMessage('Failed to fetch data. Please try again.');
    }
  }, [
    asyncResponse.error,
    asyncPayments.error,
    asyncStatus.error,
    asyncReviewFinancing.error,
    dmsPullError,
  ]);

  /**
   * called when 'Save and Start eContracting' is clicked
   */
  const callReviewFinancing = async () => {
    if (!response) {
      return;
    }

    sendRicFormUpdateToCC({
      isRecalculating: true,
      ricFormValues: response,
    });

    if (isFniPresentFlowEnabled) {
      setFniPresentLoading(true);
    }
    const amplitudeData = {
      ...getUpstartOfferEventProperties({
        dealId,
        application: response.FinanceApplication,
      }),
      fniPresentFlow: isFniPresentFlowEnabled,
    };

    sendData(amplitudeEvents.DESKING_EDIT_RIC_FINISH, amplitudeData);
    sendData(
      amplitudeEventsDeskingCTR.FINANCE_START_ECONTRACT_CLICKED,
      amplitudeData,
    );

    try {
      const status = await asyncStatus.execute(response.applicationId);
      if (!isSigned(status)) {
        const result = await asyncReviewFinancing.execute(response);

        if (result.status === FinanceResponseStatus.DENIED) {
          sendReviewFinanceCompleteToCC({
            success: false,
            error: 'applicantDenied',
          });
        } else {
          sendReviewFinanceCompleteToCC({ success: true });
        }
      }

      // If fni present flow, redirect to instore
      if (isFniPresentFlowEnabled) {
        if (IS_EMBEDDED_IN_CC) {
          window.top?.location.replace(
            InstoreService.getLoanConfirmationUrl(dealId, response.id),
          );
        } else {
          window.location.replace(
            InstoreService.getLoanConfirmationUrl(dealId, response.id),
          );
        }
      }
      sendData(
        amplitudeEventsDeskingCTR.FINANCE_START_ECONTRACT_FINISHED,
        amplitudeData,
      );

      onCancel();
    } finally {
      setFniPresentLoading(false);
      sendRicFormUpdateToCC({
        isRecalculating: false,
        ricFormValues: response,
      });
    }
  };

  function renderContent() {
    const isResponseLoading = asyncResponse.loading || asyncStatus.loading;
    return (
      <div
        className="pt-15 offer-review"
        style={isEmbeddedInClosingWorkflow() ? { paddingTop: 0 } : {}}
      >
        {(!response || props.isLoading || isResponseLoading) && (
          <Alert>Loading...</Alert>
        )}
        {asyncReviewFinancing.loading &&
          response &&
          !fetchingRICDownloadURL && <Alert>Saving data...</Alert>}
        {asyncStatus.error && (
          <Alert bsStyle="danger">Failed to fetch application status.</Alert>
        )}

        {fetchingRICDownloadURL && (
          <Grid>
            <Row>
              <Col sm={12}>
                <h4 className="text-center">
                  The customer has signed the contract.
                  <Loader active inline className="ric-download-loader" />
                  <span>Fetching download URL</span>
                </h4>
              </Col>
            </Row>
          </Grid>
        )}

        {/* Wait for response and status to be loaded before rendering the form */}
        {response &&
          !isResponseLoading &&
          !asyncReviewFinancing.loading &&
          asyncStatus.result &&
          !fetchingRICDownloadURL && (
            <>
              {showDQReasonsPanel && (
                <DQReasonsPanel applicationStatus={asyncStatus.result} />
              )}
              {isSigned(asyncStatus.result) && (
                <Grid>
                  <Row>
                    <Col sm={12}>
                      <h4 className="text-center">
                        The customer has signed the contract.{' '}
                        {!_.isEmpty(ricDownloadURL) && (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={ricDownloadURL}
                          >
                            Download RIC.
                          </a>
                        )}
                      </h4>
                    </Col>
                  </Row>
                </Grid>
              )}

              {!isSigned(asyncStatus.result) &&
                showCDKSaveDealNotice &&
                !isEmbeddedInClosingWorkflow() &&
                props?.dealer?.dmsType === DMSType.CDK && (
                  <CDKSaveDealWarningModal />
                )}

              {/* Do not render the form if the contract was signed */}
              {!isSigned(asyncStatus.result) && (
                <RICForm
                  formRef={ricFormRef}
                  response={response}
                  deal={props.deal}
                  showCDKSaveDealNotice={showCDKSaveDealNotice}
                  isDMSFlowEnabled={isDMSFlowEnabled}
                  onPullFromDMS={onPullFromDMS}
                  fetchingError={errorMessage}
                  loading={asyncPayments.loading}
                  fetchResponseWithUpdatedData={fetchResponseWithUpdatedData}
                  onCancel={onCancel}
                  callReviewFinancing={callReviewFinancing}
                  state={ricState}
                  car={car}
                />
              )}
            </>
          )}
      </div>
    );
  }

  if (!hideSideBarAndHeader) {
    return (
      <SidebarContainer>
        <div className="pencil-editor-column">
          <div>
            <div className="pencil-editor-header">
              <HeaderNavigation paymentsHref={`/pencil/${dealId}`} />
              <HeaderNotice deal={props.deal} user={props.user} />
            </div>
          </div>
          {renderContent()}
        </div>
      </SidebarContainer>
    );
  }

  return (
    <div className="pencil-editor-column">
      <Backdrop
        sx={{ color: '#aaa', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={fniPresentLoading}
      >
        <Snackbar
          open
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          message={
            <span>
              <CircularProgress
                size={'16px'}
                sx={{ color: '#fff', marginRight: '10px' }}
                thickness={6}
              />
              {FNI_PRESENT_LOADING_MESSAGE}
            </span>
          }
        />
      </Backdrop>
      {renderContent()}
    </div>
  );
}

// eslint-disable-next-line
const mapStateToProps = (state: any) => ({
  isLoading: state.deals.fetching,
  car: state.deals.car,
  deal: state.deals.deal,
  dealer: state.dealers.dealer,
  user: state.deals.user,
  currentUser: state.user.currentUser,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators({ ...dealsActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OfferReview);
